<script lang="ts">
  import {
    DEFAULT_CONFIG,
    saveConfig,
    type Config,
  } from "../../state/config";
  import {
    EXTRA_PROMPT_PROFILES,
    type PromptProfile,
  } from "../../api/prompt";
  import { app } from "../../state/app.svelte";
  import { i18n, type Locale } from "../../i18n/i18n.svelte";
  import AboutView from "./AboutView.svelte";

  const fr = {
    settingsTitle: "Configuration",
    aboutTitle: "À propos & confidentialité",
    saving: "Enregistrement…",
    saved: "✓ Enregistré",
    error: "Erreur",
    aboutBtn: "À propos",
    backToSettings: "← Configuration",
    autosave: "Les modifications sont enregistrées automatiquement (localement, IndexedDB).",
    feedback: "💬 Vos retours sur l'outil sont précieux : envoyez-les à",
    language: "Langue",
    serverUrl: "URL du serveur EPFL (parsing)",
    presetsAria: "Voir les URLs prédéfinies",
    authLegend: "Authentification serveur EPFL",
    signedInAs: "✓ Connecté en tant que",
    signOut: "Se déconnecter",
    reconnecting: "Reconnexion automatique…",
    signingIn: "Connexion…",
    signIn: "Se connecter avec EPFL",
    rcpUrl: "URL RCP (LLM)",
    lexLegend: "Bulle LEX",
    lexShow: "Afficher la bulle « LEX » sous la zone de saisie",
    lexHint:
      "Pour les questions juridiques : bulle enfoncée, la recherche EPFL ne porte que sur les textes " +
      "réglementaires de Polylex (LEX, directives, annexes, lois et ordonnances fédérales) et chaque source " +
      "est nommée par son numéro de LEX. Cachée par défaut.",
    flowLegend: "Schémas de flux de données",
    flowLevel: "Niveau de détail",
    flowNone: "Aucun — pas d'instructions spécifiques aux flux de données",
    flowSimple: "Simple — flèches et nœuds uniquement",
    flowComplete: "Complet — étapes canoniques en sous-graphes",
    promptLegend: "Ajout supplémentaire dans le prompt",
    modified: "(modifié)",
    profileAria: "Profil de prompt",
    profiles: { dpo: "DPO", user: "Utilisateur standard", assistant: "Assistant administratif", specialist: "Spécialiste des services centraux" } as Record<PromptProfile, string>,
    profileConfirm: "Le contenu a été modifié. Le remplacer par le profil sélectionné ?",
    promptPlaceholder:
      "Précisions ajoutées au prompt système (style de réponse attendu, points d'attention spécifiques, doctrine métier, etc.)",
    promptHint: "Ajouté à la fin du prompt système, en mode RAG comme en chat libre.",
    profileHint: "Choisis un profil pour pré-remplir, puis modifie librement.",
    suiteLegend: "Questions automatiques",
    suiteHint:
      "Liste de questions standard exécutées séquentiellement dans la même conversation depuis le bouton " +
      "« Lancer les questions automatiques » du chat. Utile pour rejouer la même batterie de vérifications sur " +
      "plusieurs fichiers. Glisser la poignée ⠿ pour réordonner.",
    dragHandle: "Glisser pour réordonner",
    question: "Question…",
    remove: "Supprimer",
    addQuestion: "+ Ajouter une question",
  };
  const en: typeof fr = {
    settingsTitle: "Settings",
    aboutTitle: "About & privacy",
    saving: "Saving…",
    saved: "✓ Saved",
    error: "Error",
    aboutBtn: "About",
    backToSettings: "← Settings",
    autosave: "Changes are saved automatically (locally, in IndexedDB).",
    feedback: "💬 Your feedback on the tool is valuable: send it to",
    language: "Language",
    serverUrl: "EPFL server URL (parsing)",
    presetsAria: "Show preset URLs",
    authLegend: "EPFL server authentication",
    signedInAs: "✓ Signed in as",
    signOut: "Sign out",
    reconnecting: "Reconnecting automatically…",
    signingIn: "Signing in…",
    signIn: "Sign in with EPFL",
    rcpUrl: "RCP URL (LLM)",
    lexLegend: "LEX bubble",
    lexShow: "Show the “LEX” bubble under the input box",
    lexHint:
      "For legal questions: when pressed, the EPFL search covers only the Polylex regulations (LEX, " +
      "directives, annexes, federal acts and ordinances) and each source is named by its LEX number. " +
      "Hidden by default.",
    flowLegend: "Data-flow diagrams",
    flowLevel: "Level of detail",
    flowNone: "None — no data-flow-specific instructions",
    flowSimple: "Simple — arrows and nodes only",
    flowComplete: "Complete — canonical stages as subgraphs",
    promptLegend: "Additional prompt instructions",
    modified: "(modified)",
    profileAria: "Prompt profile",
    profiles: { dpo: "DPO", user: "Standard user", assistant: "Administrative assistant", specialist: "Central-services specialist" },
    profileConfirm: "The content has been modified. Replace it with the selected profile?",
    promptPlaceholder:
      "Details added to the system prompt (expected answer style, specific points of attention, domain doctrine, etc.)",
    promptHint: "Appended to the end of the system prompt, in RAG mode as in free chat.",
    profileHint: "Pick a profile to pre-fill it, then edit freely.",
    suiteLegend: "Automatic questions",
    suiteHint:
      "A list of standard questions run one after the other in the same conversation, from the " +
      "“Run automatic questions” button in the chat. Useful to replay the same set of checks on several " +
      "files. Drag the ⠿ handle to reorder.",
    dragHandle: "Drag to reorder",
    question: "Question…",
    remove: "Delete",
    addQuestion: "+ Add a question",
  };
  const t = $derived(i18n.locale === "en" ? en : fr);

  const LANGUAGES: Array<{ locale: Locale; label: string }> = [
    { locale: "fr", label: "Français" },
    { locale: "en", label: "English" },
  ];

  // Settings shown to named people only, recognised by the email of their
  // EPFL token. Hiding is cosmetic, not a permission: the values are not
  // secret, and nothing server-side depends on who sees them.
  //
  // The server and RCP URLs are developer settings — a wrong value silently
  // breaks parsing or chat. Signing in depends on neither, so the maintainer
  // can always reach them.
  const MAINTAINER = "yannick.pezeu@epfl.ch";
  // The DPO / standard-user profile swaps the whole prompt preset: only the
  // DPO and the maintainer choose it; everyone else keeps their preset and may
  // still edit the text.
  const DPO = "chiara.tanteri@epfl.ch";
  const canEditEndpoints = $derived(app.isSignedInAs(MAINTAINER));
  const canChooseProfile = $derived(app.isSignedInAs(MAINTAINER, DPO));

  // Sub-tab inside the config screen. Keeping this as local state means the
  // About page doesn't need its own routing in App.svelte and doesn't crowd
  // the main header.
  // Dans PolyChat (v3, Réglages > Avancé) : seulement ce qui n'a pas sa page
  // ailleurs — connexion EPFL et adresse RCP. Langue, profil et consigne,
  // portée LEX, schémas de flux et grilles ont chacun leur réglage v3
  // (22.10.2026) ; l'adresse du serveur y suit la page.
  let { compact = false }: { compact?: boolean } = $props();

  let tab = $state<"settings" | "about">("settings");

  const PROFILE_KEYS: PromptProfile[] = ["dpo", "user"];

  const SERVER_PRESETS: Array<{ label: string; url: string }> = [
    { label: "Local (dev)", url: "http://localhost:8000" },
    { label: "Production EPFL", url: "https://personal-rag.epfl.ch" },
  ];

  let cfg = $state<Config>({ ...DEFAULT_CONFIG, ...(app.config ?? {}) });
  let authBusy = $state(false);
  let authError = $state<string | null>(null);

  // Autosave state. Persists 500ms after the last edit. OIDC EPFL is the only
  // supported way to authenticate against the server.
  let saveStatus = $state<"idle" | "saving" | "saved" | "error">("idle");
  let saveError = $state<string | null>(null);
  let saveTimer: ReturnType<typeof setTimeout> | null = null;
  let savedTimer: ReturnType<typeof setTimeout> | null = null;
  let firstRun = true;

  $effect(() => {
    // Snapshot traverses the whole config — this resubscribes the effect to
    // every nested field, so any edit triggers the debounced save.
    const snapshot = $state.snapshot(cfg);
    if (firstRun) {
      firstRun = false;
      return;
    }
    if (saveTimer) clearTimeout(saveTimer);
    saveStatus = "saving";
    saveTimer = setTimeout(async () => {
      try {
        await saveConfig(snapshot);
        app.config = snapshot;
        saveStatus = "saved";
        saveError = null;
        if (savedTimer) clearTimeout(savedTimer);
        savedTimer = setTimeout(() => {
          if (saveStatus === "saved") saveStatus = "idle";
        }, 1500);
      } catch (err) {
        saveStatus = "error";
        saveError = err instanceof Error ? err.message : String(err);
      }
    }, 500);
  });


  let serverDropdownOpen = $state(false);
  let serverRoot = $state<HTMLDivElement | undefined>(undefined);

  function pickServerUrl(url: string) {
    cfg.serverUrl = url;
    serverDropdownOpen = false;
  }

  function onDocumentClick(e: MouseEvent) {
    const target = e.target as Node;
    if (serverDropdownOpen && serverRoot && !serverRoot.contains(target)) {
      serverDropdownOpen = false;
    }
  }

  function onDocumentKeydown(e: KeyboardEvent) {
    if (e.key === "Escape") {
      serverDropdownOpen = false;
    }
  }

  async function onLogin() {
    authBusy = true;
    authError = null;
    try {
      await app.loginEpfl();
    } catch (err) {
      authError = err instanceof Error ? err.message : String(err);
    } finally {
      authBusy = false;
    }
  }

  async function onLogout() {
    authBusy = true;
    authError = null;
    try {
      await app.logoutEpfl();
    } catch (err) {
      authError = err instanceof Error ? err.message : String(err);
    } finally {
      authBusy = false;
    }
  }

  function preventSubmit(e: SubmitEvent) {
    // No save button — Enter inside an input would otherwise reload the page.
    // The autosave $effect handles persistence.
    e.preventDefault();
  }

  // True when the textarea has diverged from the preset of the currently
  // selected profile — drives the "(modifié)" hint and the confirm prompt
  // when the user picks the other profile (so we don't silently overwrite
  // their custom edits).
  const isPromptModified = $derived(
    cfg.extraSystemPrompt !== EXTRA_PROMPT_PROFILES[cfg.promptProfile],
  );

  function selectProfile(profile: PromptProfile) {
    if (cfg.promptProfile === profile) return;
    if (
      isPromptModified &&
      !confirm(t.profileConfirm)
    ) {
      return;
    }
    cfg.promptProfile = profile;
    cfg.extraSystemPrompt = EXTRA_PROMPT_PROFILES[profile];
  }

  function addTestQuestion() {
    cfg.testSuite = [...cfg.testSuite, ""];
  }

  function removeTestQuestion(index: number) {
    cfg.testSuite = cfg.testSuite.filter((_, i) => i !== index);
  }

  function updateTestQuestion(index: number, value: string) {
    const next = [...cfg.testSuite];
    next[index] = value;
    cfg.testSuite = next;
  }

  // ---- Reorder the questions by drag & drop --------------------------------
  //
  // Pointer events on a grip rather than HTML5 draggable, which never starts
  // inside the extension side panel (same approach as the doc library). The
  // grabbed row follows the pointer live: it swaps with a neighbour once the
  // pointer crosses that neighbour's MIDPOINT — with a plain "row under the
  // pointer" rule, a short row dragged past a tall one would swap back and
  // forth on every move.
  let draggingIndex = $state<number | null>(null);

  function onQuestionGripDown(e: PointerEvent, index: number) {
    if (e.button !== 0) return;
    e.preventDefault(); // no text selection while dragging
    draggingIndex = index;
    window.addEventListener("pointermove", onQuestionDragMove);
    window.addEventListener("pointerup", endQuestionDrag);
    window.addEventListener("pointercancel", endQuestionDrag);
  }

  function onQuestionDragMove(e: PointerEvent) {
    const from = draggingIndex;
    if (from === null) return;
    const row = (document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null)
      ?.closest<HTMLElement>("[data-question-index]");
    if (!row) return;
    const to = Number(row.dataset.questionIndex);
    if (to === from) return;
    const rect = row.getBoundingClientRect();
    const mid = rect.top + rect.height / 2;
    if ((to > from && e.clientY < mid) || (to < from && e.clientY > mid)) return;
    const next = [...cfg.testSuite];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    cfg.testSuite = next;
    draggingIndex = to;
  }

  function endQuestionDrag() {
    draggingIndex = null;
    window.removeEventListener("pointermove", onQuestionDragMove);
    window.removeEventListener("pointerup", endQuestionDrag);
    window.removeEventListener("pointercancel", endQuestionDrag);
  }

  // Leaving the panel mid-drag must not leave window listeners behind.
  $effect(() => endQuestionDrag);
</script>

<svelte:window onclick={onDocumentClick} onkeydown={onDocumentKeydown} />

<div class="header-row">
  <h2>{tab === "settings" ? t.settingsTitle : t.aboutTitle}</h2>
  <div class="header-actions">
    {#if tab === "settings" && saveStatus !== "idle"}
      <span
        class="save-indicator"
        class:save-saving={saveStatus === "saving"}
        class:save-saved={saveStatus === "saved"}
        class:save-error={saveStatus === "error"}
      >
        {#if saveStatus === "saving"}
          {t.saving}
        {:else if saveStatus === "saved"}
          {t.saved}
        {:else if saveStatus === "error"}
          ⚠ {saveError ?? t.error}
        {/if}
      </span>
    {/if}
    <button
      type="button"
      class="tab-btn"
      onclick={() => (tab = tab === "settings" ? "about" : "settings")}
    >
      {tab === "settings" ? t.aboutBtn : t.backToSettings}
    </button>
  </div>
</div>

{#if tab === "about"}
  <AboutView />
{:else}
<form onsubmit={preventSubmit}>
  <p class="muted">{t.autosave}</p>

  <p class="feedback-note">
    {t.feedback}
    <a href="mailto:feedback_genai@epfl.ch">feedback_genai@epfl.ch</a>.
  </p>

  {#if !compact}
  <!-- Not part of Config: the interface language is read synchronously at
       startup, from localStorage (see i18n.svelte.ts). -->
  <fieldset class="rerank-group">
    <legend>{t.language}</legend>
    <div class="profile-row" role="radiogroup" aria-label={t.language}>
      {#each LANGUAGES as lang (lang.locale)}
        <label class="profile-option">
          <input
            type="radio"
            name="locale"
            value={lang.locale}
            checked={i18n.locale === lang.locale}
            onchange={() => i18n.set(lang.locale)}
          />
          <span lang={lang.locale}>{lang.label}</span>
        </label>
      {/each}
    </div>
  </fieldset>
  {/if}

  {#if canEditEndpoints && !compact}
  <label>
    {t.serverUrl}
    <div class="combobox" bind:this={serverRoot}>
      <input
        type="url"
        bind:value={cfg.serverUrl}
        onfocus={() => (serverDropdownOpen = true)}
        required
      />
      <button
        type="button"
        class="chevron"
        aria-label={t.presetsAria}
        onclick={() => (serverDropdownOpen = !serverDropdownOpen)}
      >▾</button>
      {#if serverDropdownOpen}
        <ul class="options" role="listbox">
          {#each SERVER_PRESETS as preset (preset.url)}
            <li>
              <button
                type="button"
                class="option"
                class:selected={cfg.serverUrl === preset.url}
                onclick={() => pickServerUrl(preset.url)}
              >
                <span class="option-label">{preset.label}</span>
                <span class="option-sub">{preset.url}</span>
              </button>
            </li>
          {/each}
        </ul>
      {/if}
    </div>
  </label>
  {/if}

  <fieldset class="auth-group">
    <legend>{t.authLegend}</legend>
    {#if app.oidcSession}
      <div class="auth-status">
        <span class="auth-ok">{t.signedInAs} <strong>{app.oidcSession.account.email || app.oidcSession.account.name}</strong></span>
        <button type="button" class="auth-btn secondary" onclick={onLogout} disabled={authBusy}>
          {authBusy ? "…" : t.signOut}
        </button>
      </div>
    {:else if app.authReconnecting}
      <span class="auth-status">{t.reconnecting}</span>
    {:else}
      <button type="button" class="auth-btn primary" onclick={onLogin} disabled={authBusy}>
        {authBusy ? t.signingIn : t.signIn}
      </button>
    {/if}
    {#if authError}
      <p class="auth-error">{authError}</p>
    {/if}
  </fieldset>

  {#if canEditEndpoints}
    <label>
      {t.rcpUrl}
      <input type="url" bind:value={cfg.rcpUrl} required />
    </label>
  {/if}

  <!-- Thinking, EPFL sources and web search are set from the toggles under the
       chat input only: a second copy here was confusing (two places, one
       setting). The tutorial and the toggles' tooltips explain them. -->

  {#if !compact}
  <!-- Exception à la règle ci-dessus : on ne règle pas ici la source LEX, on
       décide seulement si sa bulle existe (utile aux juristes, du bruit pour
       les autres). -->
  <fieldset class="rerank-group">
    <legend>{t.lexLegend}</legend>
    <label class="check-row">
      <input type="checkbox" bind:checked={cfg.lexButtonVisible} />
      <span>{t.lexShow}</span>
    </label>
    <span class="hint">{t.lexHint}</span>
  </fieldset>

  <fieldset class="rerank-group">
    <legend>{t.flowLegend}</legend>
    <label>
      <span>{t.flowLevel}</span>
      <select bind:value={cfg.dataFlowMode}>
        <option value="none">{t.flowNone}</option>
        <option value="simple">{t.flowSimple}</option>
        <option value="complete">{t.flowComplete}</option>
      </select>
      <span class="hint">
        {#if i18n.locale === "en"}
        <strong>None</strong> (the default) adds no instructions specific to data-flow diagrams. In <strong>complete</strong> mode, each canonical stage (sources, collection, pseudonymisation, storage, analysis, archiving) is wrapped in a labelled box. More instructive, but a denser rendering. <strong>Simple</strong> mode shows only the actors and the arrows.
        {:else}
        Le mode <strong>aucun</strong> (par défaut) n'ajoute aucune consigne dédiée aux schémas de flux de données. En mode <strong>complet</strong>, chaque étape canonique (sources, collecte, pseudonymisation, stockage, analyse, archivage) est entourée d'une boîte étiquetée. Plus pédagogique mais rendu plus dense. Le mode <strong>simple</strong> ne montre que les acteurs et les flèches.
        {/if}
      </span>
    </label>
  </fieldset>

  <fieldset class="rerank-group">
    <legend>
      {t.promptLegend}
      {#if canChooseProfile && isPromptModified}<span class="optional">{t.modified}</span>{/if}
    </legend>

    {#if canChooseProfile}
    <div class="profile-row" role="radiogroup" aria-label={t.profileAria}>
      {#each PROFILE_KEYS as profile (profile)}
        <label class="profile-option">
          <input
            type="radio"
            name="promptProfile"
            value={profile}
            checked={cfg.promptProfile === profile}
            onchange={() => selectProfile(profile)}
          />
          <span>{t.profiles[profile]}</span>
        </label>
      {/each}
    </div>
    {/if}

    <textarea
      bind:value={cfg.extraSystemPrompt}
      rows="14"
      placeholder={t.promptPlaceholder}
    ></textarea>
    <span class="hint">{t.promptHint}{canChooseProfile ? ` ${t.profileHint}` : ""}</span>
  </fieldset>

  <fieldset class="rerank-group" class:reordering={draggingIndex !== null}>
    <legend>{t.suiteLegend}</legend>
    <span class="hint">{t.suiteHint}</span>

    {#each cfg.testSuite as question, i (i)}
      <div class="test-row" class:dragging={draggingIndex === i} data-question-index={i}>
        <span
          class="grip"
          title={t.dragHandle}
          aria-hidden="true"
          onpointerdown={(e) => onQuestionGripDown(e, i)}
        >⠿</span>
        <span class="test-index">{i + 1}.</span>
        <textarea
          rows="2"
          value={question}
          oninput={(e) => updateTestQuestion(i, (e.currentTarget as HTMLTextAreaElement).value)}
          placeholder={t.question}
        ></textarea>
        <div class="test-actions">
          <button
            type="button"
            class="test-btn test-remove"
            title={t.remove}
            aria-label={t.remove}
            onclick={() => removeTestQuestion(i)}
          >✕</button>
        </div>
      </div>
    {/each}

    <button type="button" class="test-add" onclick={addTestQuestion}>
      {t.addQuestion}
    </button>
  </fieldset>
  {/if}

</form>
{/if}

<style>
  form {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    padding: 0.75rem 1rem 1rem;
  }

  h2 {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
  }

  label {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    font-size: 0.8rem;
    color: var(--epfl-gray-600);
  }

  input,
  textarea {
    padding: 0.5rem;
    border: 1px solid var(--epfl-gray-200);
    border-radius: var(--epfl-radius);
    font-size: 0.85rem;
    font-family: inherit;
  }

  textarea {
    resize: vertical;
    min-height: 4.5rem;
    line-height: 1.4;
  }

  .muted {
    color: var(--epfl-gray-400);
    font-size: 0.8rem;
    margin: 0;
  }

  .hint {
    font-size: 0.7rem;
    color: var(--epfl-gray-400);
  }


  .combobox {
    position: relative;
    display: flex;
    align-items: stretch;
  }

  .combobox input {
    flex: 1;
    padding-right: 2rem;
  }

  .chevron {
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    width: 2rem;
    background: none;
    border: none;
    color: var(--epfl-gray-400);
    cursor: pointer;
    font-size: 0.8rem;
  }

  .chevron:hover {
    color: var(--epfl-text);
  }

  .options {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    margin-top: 0.2rem;
    background: var(--epfl-white);
    border: 1px solid var(--epfl-gray-200);
    border-radius: var(--epfl-radius);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    list-style: none;
    padding: 0.2rem 0;
    max-height: 12rem;
    overflow-y: auto;
    z-index: 20;
  }

  .options li {
    margin: 0;
  }

  .option {
    display: block;
    width: 100%;
    text-align: left;
    background: none;
    border: none;
    padding: 0.4rem 0.6rem;
    font-family: inherit;
    font-size: 0.8rem;
    color: var(--epfl-text);
    cursor: pointer;
    word-break: break-all;
  }

  .option:hover {
    background: var(--epfl-gray-100);
  }

  .option.selected {
    background: var(--epfl-gray-50);
    color: var(--epfl-accent-dark);
    font-weight: 500;
  }

  .option-label {
    display: block;
    font-weight: 500;
  }

  .option-sub {
    display: block;
    font-size: 0.7rem;
    color: var(--epfl-gray-400);
    margin-top: 0.1rem;
  }

  .option.selected .option-sub {
    color: var(--epfl-accent);
  }

  .rerank-group {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    border: 1px solid var(--epfl-gray-100);
    border-radius: var(--epfl-radius);
    padding: 0.55rem 0.75rem 0.7rem;
    margin: 0;
  }

  .check-row {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0.45rem;
  }

  .rerank-group legend {
    font-size: 0.75rem;
    font-weight: 500;
    color: var(--epfl-gray-600);
    padding: 0 0.3rem;
  }


  .auth-group {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    border: 1px solid var(--epfl-gray-100);
    border-radius: var(--epfl-radius);
    padding: 0.55rem 0.75rem 0.7rem;
    margin: 0;
  }

  .auth-group legend {
    font-size: 0.75rem;
    font-weight: 500;
    color: var(--epfl-gray-600);
    padding: 0 0.3rem;
  }

  .auth-status {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    flex-wrap: wrap;
  }

  .auth-ok {
    font-size: 0.85rem;
    color: var(--epfl-success-text);
  }

  .auth-btn {
    margin-top: 0;
    padding: 0.4rem 0.8rem;
    font-size: 0.8rem;
    border-radius: var(--epfl-radius);
    border: none;
    cursor: pointer;
  }

  .auth-btn.primary {
    background: var(--epfl-accent);
    color: var(--epfl-white);
    font-weight: 700;
    align-self: flex-start;
  }

  .auth-btn.primary:hover:not(:disabled) {
    background: var(--epfl-accent-dark);
  }

  .auth-btn.primary:active:not(:disabled) {
    background: var(--epfl-accent-dark);
  }

  .auth-btn.secondary {
    background: var(--epfl-gray-50);
    color: var(--epfl-text);
  }

  .auth-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .auth-error {
    margin: 0;
    font-size: 0.75rem;
    color: var(--epfl-danger-text);
  }

  .optional {
    color: var(--epfl-gray-400);
    font-weight: 400;
    font-size: 0.75rem;
  }

  .header-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    padding: 1rem 1rem 0;
  }

  .header-actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .tab-btn {
    background: none;
    border: 1px solid var(--epfl-gray-200);
    padding: 0.3rem 0.6rem;
    border-radius: var(--epfl-radius);
    font-size: 0.75rem;
    cursor: pointer;
    font-family: inherit;
    color: var(--epfl-gray-600);
  }

  .tab-btn:hover {
    background: var(--epfl-gray-100);
    color: var(--epfl-text);
  }

  .save-indicator {
    font-size: 0.72rem;
    color: var(--epfl-gray-400);
    transition: color 0.2s;
  }

  .save-indicator.save-saving {
    color: var(--epfl-gray-400);
  }

  .save-indicator.save-saved {
    color: var(--epfl-success-text);
  }

  .save-indicator.save-error {
    color: var(--epfl-danger-text);
  }

  .profile-row {
    display: flex;
    flex-wrap: wrap;
    gap: 0.6rem;
    margin-bottom: 0.2rem;
  }

  .profile-option {
    flex-direction: row !important;
    align-items: center;
    gap: 0.35rem;
    color: var(--epfl-text);
    font-size: 0.82rem;
    cursor: pointer;
  }

  .profile-option input[type="radio"] {
    width: auto;
    margin: 0;
    padding: 0;
  }

  .feedback-note {
    margin: 0;
    padding: 0.55rem 0.7rem;
    background: var(--epfl-gray-50);
    border: 1px solid var(--epfl-gray-200);
    border-radius: var(--epfl-radius);
    font-size: 0.8rem;
    color: var(--epfl-accent-dark);
    line-height: 1.35;
  }

  .feedback-note a {
    color: var(--epfl-accent);
    text-decoration: underline;
  }

  .test-row {
    display: flex;
    align-items: flex-start;
    gap: 0.4rem;
    margin-top: 0.3rem;
  }

  .test-row textarea {
    flex: 1;
    min-height: 2.6rem;
    resize: vertical;
  }

  .test-row.dragging {
    background: var(--epfl-accent-bg);
    outline: 1px dashed var(--epfl-accent);
    outline-offset: 2px;
    border-radius: var(--epfl-radius);
  }

  .reordering,
  .reordering * {
    cursor: grabbing !important;
    user-select: none;
  }

  .grip {
    color: var(--epfl-gray-200);
    cursor: grab;
    font-size: 0.95rem;
    line-height: 1;
    margin-top: 0.5rem;
    padding: 0 0.1rem;
    user-select: none;
    touch-action: none;
  }

  .test-row:hover .grip,
  .test-row.dragging .grip {
    color: var(--epfl-gray-600);
  }

  .test-index {
    font-size: 0.75rem;
    color: var(--epfl-gray-400);
    margin-top: 0.55rem;
    min-width: 1.4rem;
  }

  .test-actions {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
  }

  .test-btn {
    background: var(--epfl-gray-50);
    border: 1px solid var(--epfl-gray-200);
    border-radius: var(--epfl-radius);
    padding: 0.15rem 0.35rem;
    font-size: 0.7rem;
    line-height: 1;
    color: var(--epfl-gray-600);
    cursor: pointer;
    font-family: inherit;
  }

  .test-btn:hover:not(:disabled) {
    background: var(--epfl-gray-100);
    color: var(--epfl-text);
  }

  .test-btn.test-remove {
    color: var(--epfl-danger-text);
  }

  .test-btn.test-remove:hover:not(:disabled) {
    background: var(--epfl-danger-bg);
    color: var(--epfl-danger-text);
  }

  .test-add {
    align-self: flex-start;
    margin-top: 0.4rem;
    background: none;
    border: 1px dashed var(--epfl-gray-200);
    border-radius: var(--epfl-radius);
    padding: 0.35rem 0.6rem;
    font-size: 0.78rem;
    color: var(--epfl-gray-600);
    cursor: pointer;
    font-family: inherit;
  }

  .test-add:hover {
    background: var(--epfl-gray-50);
    color: var(--epfl-text);
  }
</style>
